import { Box, BoxProps, CheckBox, DateInput, Menu, Select, Spinner, Text } from 'grommet';
import { More } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { SessionActivityRow } from '.';
import { AddNewButton, ConfirmModal, CostTier, FieldTier, LabeledField, tableHeaderTextProps } from '../..';

import { sessionEstimateItemTitles, WorkTimeType } from '@shared/types';
import {
  Commercial,
  Location,
  Role,
  Session,
  SessionActivity,
  SessionActivityType,
  SessionEstimateItemUnitType,
  TUpdateSessionRequest,
} from '@talon/web/lib/api';
import {
  useCommercialStore,
  useEstimateStore,
  useProjectStore,
  useRoleStore,
  useSessionActivityStore,
  useSessionStore,
} from '@talon/web/src/context';
import { getCommercialRoles } from '@talon/web/src/utils/activityRoles';
import { USD } from '@talon/web/src/utils/currency';
import dayjs from 'dayjs';
import { ContractSelect } from '../estimate/ContractSelect';
import { PremiumDayPill } from '../estimate/PremiumDayPill';
import { byPosition } from '@talon/web/src/utils/stableOrder';

export interface ISessionRowProps {
  sessionId: string;
  onChecked: () => void;
  onDelete: () => Promise<void>;
  checked: boolean;
  locations: Location[];
  border?: BoxProps['border'];
  rowIndex?: number;
  onChange?: () => void;
  onSaveComplete?: () => void;
}

export const SessionRow = observer(
  ({
    sessionId,
    locations,
    onChange,
    onSaveComplete,
    onDelete,
  }: ISessionRowProps) => {
    /** Refs **/

    /** Context **/
    const commercialStore = useCommercialStore();
    const projectStore = useProjectStore();
    const sessionStore = useSessionStore();
    const estimateStore = useEstimateStore();
    const roleStore = useRoleStore();
    const sessionActivityStore = useSessionActivityStore();

    /** State **/
    const [session, setSession] = useState<Session | undefined>(undefined);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [roles, setRoles] = useState<Role[]>([]);
    const [commercials, setCommercials] = useState<Commercial[]>([]);
    const [straightHours, setStraightHours] = useState(0);
    const [overtimeHours, setOvertimeHours] = useState(0);

    /** Computed **/
    const tempEstimate = estimateStore.tempEstimate;
    const sessionEstimate = estimateStore.getTempSessionEstimate(session?.id);

    /** Methods **/

    const recalculateSessionEstimate = async () => {
      if (!session) return;

      const sessionEstimate = tempEstimate?.session_estimates.find(
        (sessionEstimate) => sessionEstimate.session?.id === session.id
      );

      if (sessionEstimate) {
        await estimateStore.updateSessionEstimate(sessionEstimate.id, undefined, undefined, undefined, true);
      }
      onSaveComplete?.();
    };

    /**
     * Update Session
     */
    const updateSession = async (update: TUpdateSessionRequest) => {
      if (!session) {
        console.log('no session');
        return;
      }
      onChange?.();
      const updatedSession = await sessionStore.updateSession(session.id, update);

      if (updatedSession) {
        setSession(updatedSession);

        // Refresh the temp estimate
        await recalculateSessionEstimate();
      }
    };

    /**
     * Add Session Activity
     */
    const addSessionActivity = async () => {
      if (!session) return;
      onChange?.();
      const previousActivity: SessionActivity | undefined = session.activities[session.activities.length - 1];
      const startTime = dayjs(previousActivity?.end_time ?? `${session.date}T09:00:00`).toISOString();
      const endTime = previousActivity
        ? dayjs(previousActivity.end_time).add(1, 'hour').toISOString()
        : new Date(`${session.date}T17:00:00`).toISOString();
      const sessionActivity = await sessionActivityStore.createSessionActivity(
        session.id,
        SessionActivityType.WorkTime,
        startTime,
        endTime,
        session.script?.id
      );
      if (sessionActivity) {
        const activities = [...session.activities, sessionActivity];
        const updatedSession = { ...session, activities };

        const sessionEstimates = (await sessionStore.getSession(session.id, 'session_estimates'))?.session_estimates;
        if (sessionEstimates) updatedSession.session_estimates = sessionEstimates;

        setSession(updatedSession);

        // Refresh the temp estimate
        await recalculateSessionEstimate();
      }
    };

    /**
     * Update Session Activity
     */
    const updateSessionActivity = async (update: SessionActivity) => {
      if (!session) return;
      onChange?.();
      const sa = await sessionActivityStore.updateSessionActivity(update);
      if (!sa) return;

      const activities = session.activities?.map((a) => (a.id === sa.id ? sa : a));
      const updatedSession = { ...session, activities };

      setSession(updatedSession);

      // Refresh the temp estimate
      await recalculateSessionEstimate();
    };

    /**
     * Delete Session Activity
     */
    const deleteSessionActivity = async (activityId: string) => {
      if (!session) return false;
      const deleted = await sessionActivityStore.deleteSessionActivity(activityId);
      if (!deleted) return false;
      onChange?.();

      const activities = session?.activities?.filter((a) => a.id !== activityId);
      const updatedSession = { ...session, activities };

      setSession(updatedSession);

      // Refresh the temp estimate
      await recalculateSessionEstimate();
      return true;
    };

    /** Effects **/

    useEffect(() => {
      const getSession = async () => {
        const s = await sessionStore.getSession(sessionId);
        s?.activities?.sort(byPosition);
        s && setSession(s);
      };
      getSession();
    }, [sessionId]);

    useEffect(() => {
      const getDropDownOptions = async () => {
        if (!projectStore.currentProject) return;
        if (!session) return;
        const [r, c] = await Promise.all([
          roleStore.getRolesByProjectId(projectStore.currentProject.id),
          commercialStore.listCommercialsByProjectId(projectStore.currentProject.id),
        ]);
        setRoles(getCommercialRoles(session.commercial_type, r ?? []));
        setCommercials(c || []);
      };
      getDropDownOptions();
    }, [projectStore.currentProject?.id, session?.activities]);

    useEffect(() => {
      if (sessionEstimate) {
        let st = 0;
        let ot = 0;

        for (const item of sessionEstimate.items) {
          if (item.unit_type === SessionEstimateItemUnitType.Hours) {
            const title = item.title.toLocaleLowerCase();
            if (title.includes(sessionEstimateItemTitles[WorkTimeType.StraightTime].toLocaleLowerCase())) {
              st += item.units ?? 0;
            } else if (
              title.includes(sessionEstimateItemTitles[WorkTimeType.TimeAndOneHalf].toLocaleLowerCase()) ||
              title.includes(sessionEstimateItemTitles[WorkTimeType.DoubleTime].toLocaleLowerCase())
            ) {
              ot += item.units ?? 0;
            }
          }
        }

        setStraightHours(st);
        setOvertimeHours(ot);
      }
    }, [sessionEstimate]);

    /** Render **/

    // Radio and AFM sessions aren't paid as straight time or overtime: show the hours scheduled instead
    const scheduledHours = session?.activities?.reduce((total, activity) => total + (activity.hours ?? 0), 0) ?? 0;
    const hoursSummary =
      [straightHours && `${straightHours} hrs straight`, overtimeHours && `${overtimeHours} hrs overtime`]
        .filter(Boolean)
        .join(' · ') || (scheduledHours ? `${scheduledHours} hrs scheduled` : 'No hours yet');
    const sessionDate = dayjs(session?.date).format('MM/DD/YYYY');

    return (
      <Box
        round="10px"
        flex="grow"
        background="white"
        border={{ color: 'border-card' }}
      >
        {/* Tier 1: the session's settings, as labelled fields */}
        <FieldTier>
          <LabeledField label="Date" width="small">
            <DateInput
              value={session && new Date(`${session.date}T00:00:00`).toISOString()}
              format="mm/dd/yyyy"
              calendarProps={{ disabled: sessionStore.getSessionDates(sessionId) }}
              onChange={(event) => session && typeof event.value === 'string' && updateSession({ date: event.value })}
            />
          </LabeledField>
          <LabeledField label="Commercial">
            <Select
              value={session?.commercial}
              onChange={({ value }) => updateSession({ commercialId: value.id })}
              placeholder="Choose commercial…"
              options={commercials}
              width="small"
              valueKey="id"
              labelKey="title"
            />
          </LabeledField>
          {/* Saved on the session, so estimates of it use this contract unless they choose their own */}
          <LabeledField label="Contract">
            <ContractSelect
              compact
              hideLabel
              editable={!!session}
              automaticBy="session date"
              appliedId={sessionEstimate?.rate_schedule_id}
              overrideId={session?.rate_schedule_override_id}
              onChange={(rateScheduleId) => updateSession({ rateScheduleOverrideId: rateScheduleId })}
            />
          </LabeledField>
          <Box pad={{ bottom: '8px' }}>
            <CheckBox
              label={<Text size="small">Union session</Text>}
              checked={!!session?.is_union}
              onChange={() => session && updateSession({ isUnion: !session.is_union })}
            />
          </Box>
          <PremiumDayPill date={session?.date} />
          <Box flex />
          <Menu
            icon={<More color="text-meta" />}
            a11yTitle={`More actions for the ${sessionDate} session`}
            dropAlign={{ top: 'bottom', right: 'right' }}
            dropProps={{ background: 'white', pad: 'xsmall', round: '8px', elevation: 'medium' }}
            items={[{ label: 'Delete session', onClick: () => setConfirmDelete(true) }]}
          />
        </FieldTier>

        {/* Tier 2: hours, and what the session costs */}
        <CostTier
          summary={hoursSummary}
          costs={[
            { label: 'Session wages', value: sessionEstimate ? USD(sessionEstimate.subtotal) : '—' },
            {
              label: 'Session total',
              value: sessionEstimate ? USD(sessionEstimate.subtotal_plus_fringe) : '—',
              emphasis: true,
            },
          ]}
        />

        {confirmDelete && (
          <ConfirmModal
            title={`Delete the ${sessionDate} session?`}
            content="Its activities go with it."
            confirmLabel="Delete session"
            danger
            onConfirm={() => {
              setConfirmDelete(false);
              onDelete();
            }}
            onCancel={() => setConfirmDelete(false)}
          />
        )}

        {/* Body */}
        <Box
          flex="grow"
          align={!session ? 'center' : undefined}
          pad={!session ? 'medium' : undefined}
          background="white"
          border={{ side: 'top', color: 'border-divider' }}
        >
          {!session && <Spinner size="small" />}
          {session?.activities && session?.activities.length > 0 && (
            <>
              {/* Table Header */}

              <Box
                direction="row"
                justify="start"
                align="center"
                gap="small"
                // style={{ borderTopLeftRadius: '15px', borderTopRightRadius: '15px' }}
                pad={{ vertical: 'xsmall', horizontal: 'small' }}
                background="surface-header"
                border={{ side: 'bottom', color: 'border-card' }}
              >
                <Box width="6%">
                  <Text {...tableHeaderTextProps}>Hours</Text>
                </Box>
                <Box width="16%">
                  <Text {...tableHeaderTextProps}>Roles</Text>
                </Box>
                <Box width="20%">
                  <Text {...tableHeaderTextProps}>Activity type</Text>
                </Box>
                <Box width="16%">
                  <Text {...tableHeaderTextProps}>Location</Text>
                </Box>
                {/* <Box flex /> */}
                <Box align="start" flex="grow">
                  <Text {...tableHeaderTextProps}>Hour totals</Text>
                </Box>
                <Box align="end" width="10%">
                  <Text {...tableHeaderTextProps}>Wages</Text>
                </Box>
                <Box align="end" width="10%">
                  <Text {...tableHeaderTextProps}>Total</Text>
                </Box>

                <Box width="40px" flex={false} />
              </Box>

              {/* Table Rows */}
              <Box direction="column" gap="small">
                {[...session.activities]
                  .sort(byPosition)
                  .map((activity, i) => (
                    <SessionActivityRow
                      key={activity.id}
                      rowIndex={i}
                      script={session.script}
                      session={session}
                      roles={roles}
                      locations={locations}
                      deleteSessionActivity={deleteSessionActivity}
                      activity={activity}
                      updateSessionActivity={(update) => updateSessionActivity({ ...activity, ...update })}
                    />
                  ))}
              </Box>
            </>
          )}
        </Box>

        {/* Footer */}
        <Box
          direction="row"
          pad={{ vertical: 'small', horizontal: '16px' }}
          border={{ side: 'top', color: 'border-divider' }}
        >
          <AddNewButton plain label="Add activity" onClick={() => addSessionActivity()} />
        </Box>
      </Box>
    );
  }
);
